/**
 * Sky and Stars Module
 * Renders stars on a 2D plane with rotation - fixed stars that rotate perpetually
 */

export interface SkyStarsState {
  stars: Array<{
    // 2D plane coordinates (infinite plane)
    planeX: number;
    planeY: number;
    size: number;
    baseOpacity: number;
    blinkPhase: number;
    blinkSpeed: number;
    blinkAmount: number; // How much the star blinks (0 = no blink, 1 = full blink)
    colorType: 'white' | 'red' | 'blue' | 'yellow'; // Star color tint
    colorR: number; // Red component (0-255)
    colorG: number; // Green component (0-255)
    colorB: number; // Blue component (0-255)
    colorHex: number; // Pre-computed hex color (0xRRGGBB)
    brightness: number; // Brightness multiplier (1.0 = normal, >1.0 = brighter)
    hasBlur: boolean; // Whether star has blur/glow effect (1% of stars)
    blurAmount: number; // Amount of blur (3-5px) for stars with blur
    dimVariation: number; // Dimness variation phase for brightness fluctuation (50% of stars)
    dimSpeed: number; // Speed of dimness variation
  }>;
  rotationAngle: number;
  rotationSpeed: number;
  // Culling stats (updated each frame)
  renderedCount: number;
  culledCount: number;
}

export function initSkyStars(canvas: HTMLCanvasElement, starCount: number = 10000): SkyStarsState {
  // Optimized star count - reduced from 42,000 for better performance
  // Still provides good visual coverage while maintaining smooth frame rates
  const stars: SkyStarsState['stars'] = [];

  // Calculate the area we need to cover
  // When rotated 45 degrees, we need to cover the diagonal
  const diagonal = Math.sqrt(canvas.width * canvas.width + canvas.height * canvas.height);
  // Add generous margin to ensure full coverage at all rotation angles
  const margin = diagonal * 1.2;
  const minX = -margin;
  const maxX = canvas.width + margin;
  const minY = -margin;
  const maxY = canvas.height + margin;

  // Calculate area to fill
  const areaWidth = maxX - minX;
  const areaHeight = maxY - minY;

  // Generate stars uniformly across the entire area
  // All stars are fixed - they exist perpetually, just rotate into/out of view
  for (let i = 0; i < starCount; i++) {
    const rand = Math.random();

    // Size variation: 1-5% from base of 1px (so 1.01 to 1.05px, but we'll use 1-1.2px range)
    const baseSize = 1;
    const sizeVariation = baseSize * (0.01 + Math.random() * 0.04); // 1-5% variation
    const size = baseSize + sizeVariation;

    // Color type distribution: mostly white, some colored
    let colorType: 'white' | 'red' | 'blue' | 'yellow' = 'white';
    let colorR = 255, colorG = 255, colorB = 255;

    if (rand < 0.15) {
      colorType = 'red';
      colorR = 255;
      colorG = 200 + Math.floor(Math.random() * 55);
      colorB = 180 + Math.floor(Math.random() * 75);
    } else if (rand < 0.25) {
      colorType = 'blue';
      colorR = 180 + Math.floor(Math.random() * 75);
      colorG = 200 + Math.floor(Math.random() * 55);
      colorB = 255;
    } else if (rand < 0.35) {
      colorType = 'yellow';
      colorR = 255;
      colorG = 240 + Math.floor(Math.random() * 15);
      colorB = 200 + Math.floor(Math.random() * 55);
    }

    // Brightness: some stars are brighter
    const brightness = Math.random() < 0.2 ? 1.3 + Math.random() * 0.4 : 1.0; // 20% are brighter

    // Blur effect: 1% of stars have blur/glow
    const hasBlur = Math.random() < 0.01;
    const blurAmount = hasBlur ? 4 + Math.random() * 3 : 0; // 4-7px blur for blurred stars (more noticeable)

    // Dim variation: 50% of all stars vary in brightness (more noticeable)
    const hasDimVariation = Math.random() < 0.5; // 50% of stars have dim variation
    const dimVariation = hasDimVariation ? Math.random() * Math.PI * 2 : 0;
    const dimSpeed = hasDimVariation ? Math.random() * 0.003 + 0.002 : 0; // More noticeable dimming speed

    // Blink variation: make blinking more noticeable
    const blinkAmount = Math.random() < 0.5 ? 0.5 + Math.random() * 0.4 : 0.3 + Math.random() * 0.3; // More stars blink noticeably (50% blink more)
    const blinkSpeed = Math.random() < 0.4 ? 0.005 + Math.random() * 0.006 : 0.002 + Math.random() * 0.004; // More noticeable blink speed

    stars.push({
      planeX: minX + Math.random() * areaWidth,
      planeY: minY + Math.random() * areaHeight,
      size: size,
      baseOpacity: (Math.random() * 0.5 + 0.4) * brightness, // 0.4 to 0.9, multiplied by brightness
      blinkPhase: Math.random() * Math.PI * 2,
      blinkSpeed: blinkSpeed,
      blinkAmount: blinkAmount,
      colorType: colorType,
      colorR: colorR,
      colorG: colorG,
      colorB: colorB,
      colorHex: (colorR << 16) | (colorG << 8) | colorB, // Pre-compute hex color
      brightness: brightness,
      hasBlur: hasBlur,
      blurAmount: blurAmount,
      dimVariation: dimVariation,
      dimSpeed: dimSpeed,
    });
  }

  return {
    stars,
    rotationAngle: 0,
    rotationSpeed: 0.0003,
    renderedCount: 0,
    culledCount: 0,
  };
}

// Project 2D plane coordinates to screen coordinates with rotation
function projectToScreen(
  planeX: number,
  planeY: number,
  centerX: number,
  centerY: number,
  rotationAngle: number
): { x: number; y: number } {
  // Rotate around center
  const cos = Math.cos(rotationAngle);
  const sin = Math.sin(rotationAngle);

  // Translate to center, rotate, translate back
  const dx = planeX - centerX;
  const dy = planeY - centerY;

  const rotatedX = dx * cos - dy * sin + centerX;
  const rotatedY = dx * sin + dy * cos + centerY;

  return { x: rotatedX, y: rotatedY };
}

export function renderSkyStars(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  state: SkyStarsState,
  time: number,
  parallaxOffset: number = 0
): void {
  // Pure black background to match site
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Update rotation angle
  state.rotationAngle += state.rotationSpeed;

  // Get device pixel ratio for coordinate conversion
  // All coordinates must be in device pixels for consistency
  const dpr = window.devicePixelRatio || 1;

  // Rotation center should be at viewport center, not canvas center
  // This keeps rotation centered in the visible area
  // Both centerX and centerY must be in device pixels
  const centerX = canvas.width / 2;
  const viewportHeightDevice = window.innerHeight * dpr; // Convert to device pixels
  const centerY = viewportHeightDevice / 2;

  // Pre-compute rotation values once per frame
  const cos = Math.cos(state.rotationAngle);
  const sin = Math.sin(state.rotationAngle);

  // Cache canvas dimensions for visibility check
  const canvasWidth = canvas.width;

  // Calculate viewport bounds in DEVICE PIXEL coordinates
  // parallaxY = rotatedY + parallaxOffset, so a star appears on screen when:
  // 0 <= parallaxY <= viewportHeightDevice (device pixel coordinates)
  // We want to know which parallaxY values are visible, so the bounds are simply:
  // visible when parallaxY is between 0 and viewportHeightDevice (with margin for smooth edges)
  const visibleTop = -50 * dpr; // Small margin above viewport (in device pixels)
  const visibleBottom = viewportHeightDevice + 50 * dpr; // Small margin below viewport

  // Track current fill style to minimize state changes
  let currentColorR = -1;
  let currentColorG = -1;
  let currentColorB = -1;

  // Track culling stats
  let renderedCount = 0;

  // Render only stars in the visible viewport
  // This reduces rendering by ~60% compared to checking full canvas height
  // Parallax offset shifts stars vertically (negative = moves up as we scroll down)
  for (const star of state.stars) {
    // Inline rotation calculation (avoid function call overhead)
    const dx = star.planeX - centerX;
    const dy = star.planeY - centerY;
    const rotatedX = dx * cos - dy * sin + centerX;
    const rotatedY = dx * sin + dy * cos + centerY;

    // Apply parallax offset (stars move slower than scroll)
    // parallaxOffset is in CSS pixels, convert to device pixels
    const parallaxY = rotatedY + (parallaxOffset * dpr);

    // Only render if visible in viewport (with small margin for smooth edges)
    // parallaxY is already in screen coordinates, so check against viewport bounds
    if (
      rotatedX >= -5 &&
      rotatedX <= canvasWidth + 5 &&
      parallaxY >= visibleTop &&
      parallaxY <= visibleBottom
    ) {
      // Calculate blinking opacity (stars blink, but don't appear/disappear) - more noticeable
      const blinkValue = Math.sin(time * star.blinkSpeed + star.blinkPhase);
      const blinkOpacity = blinkValue * star.blinkAmount * 0.4; // Increased for more noticeable blinking

      // Calculate dim variation (brightness fluctuation) for 50% of stars - more noticeable
      let dimVariationOpacity = 0;
      if (star.dimSpeed > 0) {
        const dimValue = Math.sin(time * star.dimSpeed + star.dimVariation);
        dimVariationOpacity = dimValue * 0.3; // Increased for more noticeable dimming
      }

      // Combine base opacity, blinking, and dim variation
      const opacity = Math.max(0.15, Math.min(1, star.baseOpacity + blinkOpacity + dimVariationOpacity));

      // Only change fillStyle if color changed (reduces state changes)
      if (star.colorR !== currentColorR || star.colorG !== currentColorG || star.colorB !== currentColorB) {
        ctx.fillStyle = `rgb(${star.colorR},${star.colorG},${star.colorB})`;
        currentColorR = star.colorR;
        currentColorG = star.colorG;
        currentColorB = star.colorB;
      }

      const renderSize = Math.max(1, star.size);

      // Stars that would have blur are rendered slightly larger and brighter
      const effectiveSize = star.hasBlur ? renderSize * 1.3 : renderSize;
      const effectiveOpacity = star.hasBlur ? Math.min(1, opacity * 1.2) : opacity;
      ctx.globalAlpha = effectiveOpacity;

      // Use sub-pixel positioning (floats) for smooth movement - no rounding
      // This prevents blocky/snapping movement during rotation
      if (effectiveSize <= 1.1) {
        // Small stars (1px or slightly larger) - use exact float positions
        ctx.fillRect(rotatedX, parallaxY, 1, 1);
      } else {
        // Larger stars - use exact float positions for center
        ctx.beginPath();
        ctx.arc(rotatedX, parallaxY, effectiveSize / 2, 0, Math.PI * 2);
        ctx.fill();
      }
      renderedCount++;
    }
  }

  // Update culling stats
  state.renderedCount = renderedCount;
  state.culledCount = state.stars.length - renderedCount;

  ctx.globalAlpha = 1.0;
}


